/* FILE: assets/sim_slide_mask_controller.js
   Simbiosis timed dimmer mask controller

   Key behavior:
   - For 2+ slides: start mask AFTER slide transition completes.
   - For 1 slide: mask loops, and is re-synced to the VIDEO loop (video is master clock):
     whenever the video loops back to ~0, mask animation is forced to t=0.

   Other behavior:
   - On mask start: video resets to t=0 immediately; play() async.
   - Pause mid-slide: snap open and suppress fade-in until slide changes.

   NEW (Video freeze):
   - Per-slide option: "pause video at timestamp"
   - If enabled, when video reaches pauseAt:
       - video pauses
       - mask animation pauses (optional)
       - if single-slide AND hold_single is true, dispatch sim:autoplay:pause
   - In multi-slide mode, the slide transition timer is NOT paused.

   Emits:
   - "sim:autoplay:pause"
   - "sim:autoplay:play"
*/

(() => {
  const ROOT_SELECTOR = "[data-sim-slideshow]";

  const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);
  const num = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function getSlides(root) {
    return Array.from(root.querySelectorAll("slideshow-slide:not([hidden])"));
  }

  function getActiveSlide(root) {
    return (
      root.querySelector("slideshow-slide[aria-hidden='false']") ||
      root.querySelector("slideshow-slide:not([aria-hidden='true'])") ||
      root.querySelector("slideshow-slide")
    );
  }

  function slideCount(root) {
    return root.querySelectorAll("slideshow-slide:not([hidden])").length;
  }

  function getDuration(root, slide) {
    const fallback = clamp(num(root.dataset.simAutoplayDefault, 4), 1, 45);
    const meta = slide?.querySelector("[data-sim-slide-meta]");
    return clamp(num(meta?.dataset?.slideDuration, fallback), 1, 45);
  }

  function readCfg(maskEl, durationSec) {
    const d = clamp(durationSec, 1, 45);

    let outStart = clamp(num(maskEl.dataset.maskOutStart, 0), 0, d);
    let outEnd = clamp(num(maskEl.dataset.maskOutEnd, 1), 0, d);

    let inStart = clamp(
      num(maskEl.dataset.maskInStart, Math.max(0, d - 1)),
      0,
      d
    );
    let inEnd = clamp(num(maskEl.dataset.maskInEnd, d), 0, d);

    outEnd = Math.max(outStart, outEnd);
    inStart = Math.max(outEnd, inStart);
    inEnd = Math.max(inStart, inEnd);

    const outFrom = clamp(num(maskEl.dataset.maskOutFrom, 100), 0, 100) / 100;
    const outTo = clamp(num(maskEl.dataset.maskOutTo, 0), 0, 100) / 100;
    const inFrom =
      clamp(num(maskEl.dataset.maskInFrom, outTo * 100), 0, 100) / 100;
    const inTo =
      clamp(num(maskEl.dataset.maskInTo, outFrom * 100), 0, 100) / 100;

    return { d, outStart, outEnd, inStart, inEnd, outFrom, outTo, inFrom, inTo };
  }

  function keyframes(cfg) {
    const { d, outStart, outEnd, inStart, inEnd, outFrom, outTo, inFrom, inTo } =
      cfg;

    return [
      { offset: 0, opacity: outFrom },
      { offset: outStart / d, opacity: outFrom },
      { offset: outEnd / d, opacity: outTo },
      { offset: inStart / d, opacity: inFrom },
      { offset: inEnd / d, opacity: inTo },
      { offset: 1, opacity: inTo }
    ];
  }

  function cancelAnim(maskEl) {
    const anim = maskEl?._simMaskAnim;
    if (!anim) return;
    try {
      anim.cancel();
    } catch (e) {}
    maskEl._simMaskAnim = null;
  }

  function findSlideVideo(slide) {
    const container = slide?.querySelector(".slide__image-container");
    if (!container) return null;
    return container.querySelector("video") || null;
  }

  function hardResetVideo(video) {
    if (!video) return;
    try {
      video.pause();
      video.currentTime = 0;
    } catch (e) {}
  }

  function playVideoAsync(video) {
    if (!video) return;
    try {
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (e) {}
  }

  function pauseVideo(video) {
    if (!video) return;
    try {
      video.pause();
    } catch (e) {}
  }

  function setMaskInitialNow(maskEl, cfg) {
    if (!maskEl) return;
    maskEl.style.opacity = String(cfg.outFrom);
  }

  function setAllMasksInitialNow(root) {
    const slides = getSlides(root);
    for (const slide of slides) {
      const maskEl = slide.querySelector("[data-sim-slide-mask]");
      if (!maskEl) continue;

      const dur = getDuration(root, slide);
      const cfg = readCfg(maskEl, dur);

      // Cancel current anim and snap covered
      detachSingleSlideSync(slide);
      detachVideoFreeze(slide);
      cancelAnim(maskEl);
      setMaskInitialNow(maskEl, cfg);
    }
  }

  function clearSuppression(slide) {
    if (!slide) return;
    delete slide.dataset.simMaskSuppressed;
  }

  // ------------------------------------
  // Read per-slide video freeze settings
  // ------------------------------------
  function readVideoFreezeCfg(slide) {
    const meta = slide?.querySelector("[data-sim-slide-meta]");
    if (!meta) return null;

    const enabled = String(meta.dataset.videoPauseEnable) === "true";
    if (!enabled) return null;

    const pauseAt = clamp(num(meta.dataset.videoPauseAt, 0), 0, 45);
    // Pause at 0 is effectively useless; treat as disabled
    if (pauseAt <= 0) return null;

    const pauseMask = String(meta.dataset.videoPauseMask) !== "false";
    const holdSingle = String(meta.dataset.videoPauseHoldSingle) !== "false";

    return { pauseAt, pauseMask, holdSingle };
  }

  // -----------------------------
  // Single-slide video-master sync
  // -----------------------------
  function detachSingleSlideSync(slide) {
    const video = findSlideVideo(slide);
    if (!video) return;

    const st = video._simSyncState;
    if (!st) return;

    try {
      video.removeEventListener("timeupdate", st.onTimeUpdate);
      video.removeEventListener("seeked", st.onSeeked);
      video.removeEventListener("ended", st.onEnded);
      video.removeEventListener("play", st.onPlay);
    } catch (e) {}

    video._simSyncState = null;
  }

  function attachSingleSlideSync(root, slide, maskEl) {
    // Only if it is truly single-slide mode and we have an active anim
    if (slideCount(root) !== 1) return;
    if (!maskEl?._simMaskAnim) return;

    const video = findSlideVideo(slide);
    if (!video) return;

    // Do not attach sync if video is frozen
    if (slide.dataset.simVideoFrozen === "true") return;

    // Already attached?
    if (video._simSyncState) return;

    const state = {
      lastT: 0,
      armed: false,
      onTimeUpdate: null,
      onSeeked: null,
      onEnded: null,
      onPlay: null
    };

    const resetMaskToStart = () => {
      if (slide.dataset.simMaskSuppressed === "true") return;
      if (slide.dataset.simVideoFrozen === "true") return;

      const anim = maskEl._simMaskAnim;
      if (!anim) return;

      try {
        anim.currentTime = 0;
      } catch (e) {
        const dur = getDuration(root, slide);
        const cfg = readCfg(maskEl, dur);
        cancelAnim(maskEl);
        setMaskInitialNow(maskEl, cfg);
        maskEl._simMaskAnim = maskEl.animate(keyframes(cfg), {
          duration: cfg.d * 1000,
          easing: "linear",
          fill: "forwards",
          iterations: Infinity
        });
      }
    };

    state.onPlay = () => {
      state.armed = true;
      state.lastT = video.currentTime || 0;
    };

    state.onTimeUpdate = () => {
      if (!state.armed) return;
      if (slide.dataset.simVideoFrozen === "true") return;

      const t = video.currentTime || 0;
      if (state.lastT > 0.6 && t < 0.25 && state.lastT - t > 0.6) {
        resetMaskToStart();
      }
      state.lastT = t;
    };

    state.onSeeked = () => {
      const t = video.currentTime || 0;
      if (t < 0.25) resetMaskToStart();
      state.lastT = t;
    };

    state.onEnded = () => {
      resetMaskToStart();
    };

    video._simSyncState = state;

    video.addEventListener("play", state.onPlay, { passive: true });
    video.addEventListener("timeupdate", state.onTimeUpdate, { passive: true });
    video.addEventListener("seeked", state.onSeeked, { passive: true });
    video.addEventListener("ended", state.onEnded, { passive: true });
  }

  // -----------------------------
  // Video freeze at timestamp
  // -----------------------------
  function detachVideoFreeze(slide) {
    const video = findSlideVideo(slide);
    if (!video) return;

    const st = video._simFreezeState;
    if (!st) return;

    try {
      video.removeEventListener("timeupdate", st.onTimeUpdate);
      video.removeEventListener("seeked", st.onSeeked);
    } catch (e) {}

    video._simFreezeState = null;
  }

  function attachVideoFreeze(root, slide, maskEl) {
    const cfg = readVideoFreezeCfg(slide);
    if (!cfg) {
      slide.dataset.simVideoFrozen = "false";
      detachVideoFreeze(slide);
      return;
    }

    const video = findSlideVideo(slide);
    if (!video) return;

    // Avoid double attaching
    if (video._simFreezeState) return;

    // Reset frozen state on activation
    slide.dataset.simVideoFrozen = "false";

    const EPS = 0.06; // tolerance for timeupdate granularity

    const freezeNow = () => {
      if (slide.dataset.simVideoFrozen === "true") return;

      slide.dataset.simVideoFrozen = "true";

      // Pause video
      pauseVideo(video);

      // Pause mask animation (optional)
      if (cfg.pauseMask && maskEl?._simMaskAnim) {
        try {
          maskEl._simMaskAnim.pause();
        } catch (e) {}
      }

      // In single-slide mode, optionally pause "autoplay" semantics too
      if (slideCount(root) === 1 && cfg.holdSingle) {
        root.dispatchEvent(
          new CustomEvent("sim:autoplay:pause", { bubbles: true })
        );
      }

      // Stop sync so it doesn't try to reset mask on loop detection
      detachSingleSlideSync(slide);
    };

    const onTimeUpdate = () => {
      if (slide.dataset.simVideoFrozen === "true") return;
      const t = video.currentTime || 0;
      if (t + EPS >= cfg.pauseAt) {
        freezeNow();
      }
    };

    const onSeeked = () => {
      // If we seek beyond pauseAt while active, freeze immediately
      if (slide.dataset.simVideoFrozen === "true") return;
      const t = video.currentTime || 0;
      if (t + EPS >= cfg.pauseAt) freezeNow();
    };

    video._simFreezeState = { onTimeUpdate, onSeeked };
    video.addEventListener("timeupdate", onTimeUpdate, { passive: true });
    video.addEventListener("seeked", onSeeked, { passive: true });
  }

  // -----------------------------
  // Mask run / pause suppression
  // -----------------------------
  function runMask(root, slide) {
    const maskEl = slide?.querySelector("[data-sim-slide-mask]");
    if (!maskEl) {
      // Still apply video freeze watcher even if no mask exists
      attachVideoFreeze(root, slide, null);
      return;
    }

    if (slide.dataset.simMaskSuppressed === "true") return;

    const dur = getDuration(root, slide);
    const cfg = readCfg(maskEl, dur);

    // Start from covered state
    setMaskInitialNow(maskEl, cfg);

    // Stop prior
    detachSingleSlideSync(slide);
    detachVideoFreeze(slide);
    cancelAnim(maskEl);

    // Reset active video immediately; play async
    const video = findSlideVideo(slide);
    if (video) {
      try {
        slide.dataset.simVideoFrozen = "false";
      } catch (e) {}

      hardResetVideo(video);
      queueMicrotask(() => playVideoAsync(video));
    }

    if (prefersReducedMotion()) {
      maskEl.style.opacity = String(cfg.outTo);
      // Even under reduced motion, allow freeze
      attachVideoFreeze(root, slide, maskEl);
      return;
    }

    const looping = slideCount(root) <= 1;

    maskEl._simMaskAnim = maskEl.animate(keyframes(cfg), {
      duration: cfg.d * 1000,
      easing: "linear",
      fill: "forwards",
      iterations: looping ? Infinity : 1
    });

    // Attach freeze watcher after anim exists (so we can pause it)
    attachVideoFreeze(root, slide, maskEl);

    // Single slide: video-master sync (prevents drift) unless frozen
    if (looping && slide.dataset.simVideoFrozen !== "true") {
      attachSingleSlideSync(root, slide, maskEl);
    }
  }

  function snapOpenAndSuppress(root, slide) {
    const maskEl = slide?.querySelector("[data-sim-slide-mask]");
    if (!maskEl) return;

    const dur = getDuration(root, slide);
    const cfg = readCfg(maskEl, dur);

    detachSingleSlideSync(slide);
    detachVideoFreeze(slide);
    cancelAnim(maskEl);

    maskEl.style.opacity = String(cfg.outTo);
    slide.dataset.simMaskSuppressed = "true";
  }

  // -----------------------------
  // Transition delay logic (2+ slides)
  // -----------------------------
  function parseTimeListToMs(str) {
    if (!str) return 0;
    const parts = String(str)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    let maxMs = 0;
    for (const p of parts) {
      let ms = 0;
      if (p.endsWith("ms")) ms = parseFloat(p);
      else if (p.endsWith("s")) ms = parseFloat(p) * 1000;
      else ms = parseFloat(p) * 1000;
      if (Number.isFinite(ms)) maxMs = Math.max(maxMs, ms);
    }
    return maxMs;
  }

  function getTransitionDelayMs(root) {
    const candidates = [
      root.querySelector("slideshow-slides"),
      root.querySelector("slideshow-container"),
      root.querySelector("slideshow-component"),
      root
    ].filter(Boolean);

    let best = 0;

    for (const el of candidates) {
      const cs = getComputedStyle(el);
      const td = parseTimeListToMs(cs.transitionDuration);
      const tdel = parseTimeListToMs(cs.transitionDelay);
      const ad = parseTimeListToMs(cs.animationDuration);
      const adel = parseTimeListToMs(cs.animationDelay);
      best = Math.max(best, td + tdel, ad + adel);
    }

    return clamp(best || 650, 250, 2000);
  }

  class Controller {
    constructor(root) {
      this.root = root;
      this.mo = null;

      this.startTimer = 0;
      this.pendingTarget = null;
      this.pendingAt = 0;

      this.currentActive = null;
      this.didInit = false;

      this._onChange = this._onChange.bind(this);
      this._onClick = this._onClick.bind(this);

      this._init();
    }

    _clearStartTimer() {
      if (this.startTimer) {
        clearTimeout(this.startTimer);
        this.startTimer = 0;
      }
    }

    _scheduleStartAfterTransition(active) {
      this._clearStartTimer();

      const delay = getTransitionDelayMs(this.root) + 50;

      // debounce storms for same active slide
      const now = Date.now();
      if (this.pendingTarget === active && now - this.pendingAt < 120) return;
      this.pendingTarget = active;
      this.pendingAt = now;

      // Keep covered during transition
      const maskEl = active?.querySelector("[data-sim-slide-mask]");
      if (maskEl) {
        const dur = getDuration(this.root, active);
        const cfg = readCfg(maskEl, dur);
        detachSingleSlideSync(active);
        detachVideoFreeze(active);
        cancelAnim(maskEl);
        setMaskInitialNow(maskEl, cfg);
      } else {
        // No mask; still detach freeze so we reattach cleanly after transition
        detachVideoFreeze(active);
      }

      this.startTimer = setTimeout(() => {
        this.startTimer = 0;

        const stillActive = getActiveSlide(this.root);
        if (!stillActive || stillActive !== active) return;

        clearSuppression(active);
        runMask(this.root, active);
        this.pendingTarget = null;
      }, delay);
    }

    _onChange() {
      const slides = getSlides(this.root);

      // Deactivate: clear suppression and pause video
      for (const s of slides) {
        if (s.getAttribute("aria-hidden") === "false") continue;
        clearSuppression(s);
        detachSingleSlideSync(s);
        detachVideoFreeze(s);
        const v = findSlideVideo(s);
        if (v) pauseVideo(v);
      }

      const active = getActiveSlide(this.root);
      if (!active) return;

      const multi = slideCount(this.root) > 1;

      if (!this.didInit) {
        this.didInit = true;
        this.currentActive = active;
        this._clearStartTimer();
        clearSuppression(active);
        runMask(this.root, active);
        return;
      }

      // Single slide: run immediately (loop + video-master sync)
      if (!multi) {
        this.currentActive = active;
        this._clearStartTimer();
        clearSuppression(active);
        runMask(this.root, active);
        return;
      }

      // Multi-slide: only start after transition (prevents restart)
      if (active !== this.currentActive) {
        this.currentActive = active;
        setAllMasksInitialNow(this.root);
        this._scheduleStartAfterTransition(active);
        return;
      }

      // Same active slide + extra signals: do nothing; timer (if any) will handle.
    }

    _onClick(e) {
      const btn = e.target?.closest?.("button");
      if (!btn) return;

      const action = btn.getAttribute("on:click") || "";
      const active = getActiveSlide(this.root);

      if (action.includes("/pause")) {
        this.root.dispatchEvent(
          new CustomEvent("sim:autoplay:pause", { bubbles: true })
        );
        this._clearStartTimer();
        snapOpenAndSuppress(this.root, active);
      } else if (action.includes("/play")) {
        this.root.dispatchEvent(
          new CustomEvent("sim:autoplay:play", { bubbles: true })
        );
      }
    }

    _init() {
      this.root.addEventListener("slideshow:change", this._onChange);

      try {
        this.mo = new MutationObserver(this._onChange);
        this.mo.observe(this.root, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-hidden"]
        });
      } catch (e) {}

      this.root.addEventListener("click", this._onClick, true);

      this._onChange();
    }

    destroy() {
      this._clearStartTimer();
      this.root.removeEventListener("slideshow:change", this._onChange);
      this.root.removeEventListener("click", this._onClick, true);
      if (this.mo) this.mo.disconnect();

      const active = getActiveSlide(this.root);
      detachSingleSlideSync(active);
      detachVideoFreeze(active);
    }
  }

  const instances = new Map();

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new Controller(root));
  }

  function unmount(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.destroy();
    instances.delete(root);
  }

  function scan(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan());
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", (e) => scan(e.target || document));
  document.addEventListener("shopify:section:unload", (e) => {
    (e.target || document).querySelectorAll(ROOT_SELECTOR).forEach(unmount);
  });
})();
